import { useRef, type MouseEvent } from 'react';

/**
 * 배경(어둡게 처리된 부분)을 눌러 창을 닫는 규칙 — **누른 곳과 뗀 곳이 모두
 * 배경일 때만** 닫는다.
 *
 * ── 왜 `onClick` 하나로는 안 되나 ─────────────────────────────────────────
 * 브라우저는 누른 곳과 뗀 곳이 다르면 **둘의 공통 조상**에서 `click` 을 쏜다.
 * 그래서 입력칸 안에서 글자를 드래그하다 손을 배경에서 떼면, `click` 이 배경에서
 * 발생해 **고치던 창이 닫힌다**(오너 신고 2026-09-16). 긴 폼에서 특히 자주 난다.
 *
 * 누름(`mousedown`)과 뗌(`mouseup`)을 각각 보고 **둘 다 배경일 때만** 닫으면
 * 그 사고가 사라진다:
 *
 * | 누른 곳 | 뗀 곳   | 결과   |
 * | ------- | ------- | ------ |
 * | 배경    | 배경    | 닫는다 |
 * | 창 안   | 배경    | 안 닫음 (드래그하다 빠져나온 경우 — 신고된 사고) |
 * | 배경    | 창 안   | 안 닫음 (역방향 드래그도 실수로 본다) |
 * | 창 안   | 창 안   | 안 닫음 |
 *
 * ── 왜 배경 닫기를 아예 끄지 않나 ─────────────────────────────────────────
 * 공용 `Modal` 은 같은 사고를 만나 `closeOnBackdrop` 을 기본 false 로 껐다.
 * 이쪽은 오너가 "배경 클릭으로 닫히는 건 좋다" 고 해서 기능을 살리고 판정만
 * 정확하게 했다.
 *
 * 쓰는 법 — 배경 요소에 펼쳐 넣는다. 안쪽 패널에 `stopPropagation` 을 걸 필요가
 * 없다(`e.target === e.currentTarget` 로 배경 자신인지 보기 때문이다).
 *
 * ```tsx
 * <div {...useBackdropDismiss(onCancel)} style={{ position: 'fixed', inset: 0 }}>
 * ```
 */
export function useBackdropDismiss(onDismiss: () => void): {
  onMouseDown: (e: MouseEvent<HTMLElement>) => void;
  onMouseUp: (e: MouseEvent<HTMLElement>) => void;
} {
  // 누름이 배경에서 시작했는가. ref 인 이유: 이 값이 바뀌어도 다시 그릴 필요가
  // 없고, state 로 두면 드래그 중에 입력칸이 리렌더돼 커서가 튄다.
  const pressedOnBackdrop = useRef(false);

  return {
    onMouseDown: (e) => {
      pressedOnBackdrop.current = e.target === e.currentTarget;
    },
    onMouseUp: (e) => {
      const releasedOnBackdrop = e.target === e.currentTarget;
      const pressed = pressedOnBackdrop.current;
      // 다음 판정에 새어 나가지 않게 항상 내린다 — 창 밖에서 손을 뗀 경우처럼
      // mouseup 이 안 오는 경로가 있어서, 남겨 두면 엉뚱한 때 닫힌다.
      pressedOnBackdrop.current = false;
      if (pressed && releasedOnBackdrop) onDismiss();
    },
  };
}
